import type { PortEnv, PhpRef } from '../janus-engine/port-contract';
import type { EffectSpeedDeps } from '../janus-engine/port/k5-render/ability_effect_speed_renderers';
import type { TeamDisplayDeps } from '../janus-engine/port/k5-render/team_and_display_helpers';
import { calculateDamageDice, calculateDamageMod } from '../janus-engine/port/k5-render/ability_effect_speed_renderers';
import { fateTideSpellRowSupportingColumnsHtml } from '../janus-engine/port/k5-render/team_and_display_helpers';
import { replaceFormulaClassLevels } from '../janus-engine/compile/formula-variable-resolution';
import type { ItemFieldRow } from '../janus-engine/compile/item-field-reader';
import contract from '../janus-engine/compile/c1-contract.json';
import { PhpArray } from '../janus-engine/php-compat';
import { at, type Row } from '../janus-engine/port/k5-render/support';
import { iconPh, abilityEntryId, passiveActive } from './tabs/ability-row';
import { findFullNestedItem } from '../object-card/find-cached-item';

/** K5 is stateless. Its dependencies use the same readers as the sheet. A missing
 * dependency is a visible refusal, never a request for server-rendered output. */
export function k5Environment(editable = false): PortEnv<EffectSpeedDeps & TeamDisplayDeps> {
    const unavailable = (name: string): never => { throw new Error(`unported:creature-display:${name}`); };
    const converted = new WeakMap<object, unknown>();
    const native = (value: unknown): unknown => {
        if (!(value instanceof PhpArray)) return value;
        if (converted.has(value)) return converted.get(value);
        const pairs = [...value];
        const result = pairs.every(([key], index) => key === index) ? [] : {};
        converted.set(value, result);
        for (const [key, child] of pairs) (result as Record<string, unknown>)[String(key)] = native(child);
        return result;
    };
    const deps: Record<string, unknown> = {
        layer_value: (row: Row, key: string) => ftLayerValue(native(row), key) ?? null,
        layer_has: (row: Row, key: string) => ftLayerHas(native(row), key),
        layer_entries: (row: Row, key: string) => ftLayerEntries(native(row), key) ?? [],
        item_field: (row: Row, key: string) => ftItemsCol(native(row), key) ?? null,
        icon_ph: (name: unknown) => iconPh(String(name ?? '')),
        ability_entry_id: (entry: unknown) => abilityEntryId(native(entry)),
        passive_ability_entry_is_active: (entry: unknown) => passiveActive(native(entry)),
        replace_class_level_placeholders: (_caster: PhpRef<Row>, mod: PhpRef<unknown>) => {
            if (typeof mod.value === 'string') mod.value = replaceFormulaClassLevels(_caster.value as ItemFieldRow, mod.value, contract.rollup);
        },
    };
    const env: PortEnv<EffectSpeedDeps & TeamDisplayDeps> = {
        globals: { ft_abilities_can_edit: editable }, functionStatics: {},
        deps: new Proxy(deps, { get: (target, key) => target[String(key)] ?? (() => unavailable(String(key))) }) as unknown as EffectSpeedDeps & TeamDisplayDeps,
        rand: () => unavailable('rand'), now: () => unavailable('now'), nowMicro: () => unavailable('nowMicro'),
        out: () => unavailable('out'), uuid: () => unavailable('uuid'), uniqid: () => unavailable('uniqid'),
        exit: () => unavailable('exit'), warn: message => { throw new Error(`unported:creature-display:${message}`); },
        randomBytes: () => unavailable('randomBytes'), gmdate: () => unavailable('gmdate'), hrtime: () => unavailable('hrtime'),
    };
    deps.calculate_damage_dice = (dice: unknown, ability: Row, caster: Row) => calculateDamageDice(dice, ability, caster, env);
    deps.calculate_damage_mod = (ability: Row, caster: Row) => calculateDamageMod(ability, caster, env);
    return env;
}

export function k5SpellColumns(ability: Row): Record<string, string> {
    const columns = fateTideSpellRowSupportingColumnsHtml(ability, k5Environment());
    return Object.fromEntries(['range', 'aoe', 'duration', 'hit_dc', 'cast_time'].map(key => [key, String(at(columns, key) ?? '')]));
}

export function displayRefusal(reason: unknown): string {
    return `<span class="text-danger" data-ft-display-refusal>${escapeHtml(reason instanceof Error ? reason.message : String(reason))}</span>`;
}

/** Resolve lean refs only from the catalog/cache already held by the browser. */
const definitionIndexes = new WeakMap<object, { size: number; keys: Map<string, string> }>();

/** Retain keys for replacements; rebuild after definitions are added or removed. */
export function catalogDefinition(catalog: unknown, id: string): Record<string, unknown> | undefined {
    if (!catalog || typeof catalog !== 'object') return undefined;
    let index = definitionIndexes.get(catalog);
    const size = Object.keys(catalog).length;
    if (!index || index.size !== size) {
        index = { size, keys: new Map(Object.entries(catalog).map(([key, def]) => [String(ftItemsCol(def, 'item_id') ?? key).toUpperCase(), key])) };
        definitionIndexes.set(catalog, index);
    }
    const key = index.keys.get(id.toUpperCase());
    const row = key === undefined ? undefined : at(catalog, key);
    return row && typeof row === 'object' ? row as Record<string, unknown> : undefined;
}

export function sheetDefinition(caster: unknown, entry: unknown): Record<string, unknown> {
    const nested = at(entry, 'item_arr') ?? entry;
    if (nested && typeof nested === 'object' && at(nested, 'item_layer_types')) return nested as Record<string, unknown>;
    const id = typeof nested === 'string' ? nested : String(at(nested, 'item_id') ?? at(nested, 'items', 'item_id') ?? at(nested, 'ability_id') ?? '');
    // An encounter member carries no catalog of its own (definitions union up to the encounter root),
    // so a miss falls through to the canonical cross-root finder the action bar resolves with.
    const found = catalogDefinition(at(caster, 'catalog'), id) ?? findFullNestedItem(id) ?? undefined;
    const cached = (window.FateTide?.objectCache as Map<string, unknown> | undefined)?.get(id);
    const result = found ?? cached;
    if (!result || typeof result !== 'object') throw new Error(`unported:creature-display:definition-not-held:${id}`);
    return result as Record<string, unknown>;
}
